import { useState, useEffect } from "react";
import api, { getApiErrorMessage } from "../../lib/api";
import SaleDetailsDialog from "../../components/SaleDetailsDialog";
import SaleStatusBadge from "../../components/SaleStatusBadge";
import ScrollableTable from "../../components/ScrollableTable";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { formatCurrency, formatPoints } from "../../lib/display";
import { Search, Loader2, User, Coins, Eye, RotateCcw } from "lucide-react";

const getSaleItems = (venda) => {
  if (venda?.itens?.length) return venda.itens;
  if (venda?.produtos?.length) return venda.produtos;
  return [];
};

const getItemName = (item) => (
  item?.produto ||
  item?.descricao_produto ||
  item?.descricao ||
  item?.sku ||
  item?.codigo_produto_erp ||
  "Produto"
);

const getSaleCode = (venda) => venda?.numero_documento || venda?.id_venda_erp || venda?.id?.slice(0, 8);

const AdminVendas = () => {
  const [vendas, setVendas] = useState([]);
  const [parceirosMap, setParceirosMap] = useState({});
  const [lojasMap, setLojasMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedVenda, setSelectedVenda] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vendasRes, parceirosRes, lojasRes] = await Promise.all([
        api.get("/vendas"),
        api.get("/parceiros"),
        api.get("/lojas"),
      ]);
      
      setVendas(vendasRes.data);
      
      const pMap = {};
      parceirosRes.data.forEach(p => pMap[p.id] = p);
      setParceirosMap(pMap);

      const lMap = {};
      lojasRes.data.forEach(loja => lMap[loja.id] = loja.nome);
      setLojasMap(lMap);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar dados."));
    } finally {
      setLoading(false);
    }
  };

  const filteredVendas = vendas.filter((v) => {
    const parceiro = parceirosMap[v.parceiro_id];
    const items = getSaleItems(v);
    const searchableText = [
      parceiro?.nome,
      parceiro?.codigo_parceiro,
      v.origem,
      v.status_venda,
      v.numero_documento,
      v.id_venda_erp,
      v.cliente_erp?.nome_cliente,
      v.cliente_erp?.id_cliente_erp,
      v.cliente_erp?.cpf_cnpj_cliente,
      ...items.flatMap(item => [
        getItemName(item),
        item.sku,
        item.codigo_produto_erp,
        item.nome_marca,
      ]),
    ].filter(Boolean).join(" ").toLowerCase();

    return searchableText.includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-vendas">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Vendas</h1>
          <p className="text-slate-600 mt-1">Histórico de vendas recebidas pelo ERP</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Buscar vendas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10"
          data-testid="search-vendas-input"
        />
      </div>

      {/* Table */}
      <div className="min-w-0">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <ScrollableTable label="Vendas" count={filteredVendas.length}>
            <table className="w-full min-w-[1360px] table-fixed">
              <colgroup>
                <col className="w-[20%]" />
                <col className="w-[17%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[16%]" />
                <col className="w-[9%]" />
                <col className="w-[12%]" />
              </colgroup>
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Parceiro
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Pontos
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Estornados
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="sticky right-0 bg-slate-50 px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Detalhes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredVendas.map((venda) => {
                  const parceiro = parceirosMap[venda.parceiro_id];
                  const saleCode = getSaleCode(venda);
                  const cliente = venda.cliente_erp || {};
                  const pontosEstornados = Number(venda.pontos_estornados || 0);
                  const lojaNome = venda.loja_nome || lojasMap[venda.loja_id];
                  const vendaComLoja = lojaNome ? { ...venda, loja_nome: lojaNome } : venda;
                  
                  return (
                    <tr key={venda.id} className="group hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-slate-900">
                              {parceiro?.nome || "Desconhecido"}
                            </p>
                            {saleCode && (
                              <p className="text-xs text-slate-400">
                                Doc. {saleCode}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900">
                            {cliente.nome_cliente || "Não informado"}
                          </p>
                          <p className="text-sm text-slate-500">
                            {cliente.id_cliente_erp ? `Cód. ${cliente.id_cliente_erp}` : "Sem código"}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="font-semibold text-slate-900">
                          {formatCurrency(venda.valor_total)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Coins className="h-4 w-4 text-yellow-500" />
                          <span className="font-semibold text-green-600">
                            +{formatPoints(venda.pontos_gerados)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {pontosEstornados > 0 ? (
                          <div className="flex items-center gap-1 text-amber-700">
                            <RotateCcw className="h-4 w-4" />
                            <span className="font-semibold">-{formatPoints(pontosEstornados)}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <SaleStatusBadge status={venda.status_venda} />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-slate-600">
                        {new Date(venda.data_venda || venda.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="sticky right-0 bg-white px-4 py-4 text-right whitespace-nowrap shadow-[-3px_0_6px_-4px_rgba(0,0,0,0.2)] group-hover:bg-slate-50">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedVenda(vendaComLoja)}
                          data-testid={`sale-details-${venda.id}`}
                        >
                          <Eye className="mr-1 h-4 w-4" />
                          Ver detalhes
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {filteredVendas.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      {search ? "Nenhuma venda encontrada" : "Nenhuma venda registrada ainda"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </ScrollableTable>
        )}
      </div>

      <SaleDetailsDialog
        open={Boolean(selectedVenda)}
        onOpenChange={(open) => !open && setSelectedVenda(null)}
        venda={selectedVenda}
        showCustomerDocument
      />
    </div>
  );
};

export default AdminVendas;
